import React, { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet";
import "./CheatRequests.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSquarePlus } from "@fortawesome/free-solid-svg-icons";

function CheatRequests() {
  const [steamLink, setSteamLink] = useState("");
  const [cheatDetails, setCheatDetails] = useState("");
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [voteLoading, setVoteLoading] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [formVisible, setFormVisible] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const requestsPerPage = 5;
  const navigate = useNavigate();
  localStorage.setItem("redirectAfterLogin", window.location.pathname);

  // (unused in your snippet but keeping as-is)
  const [provideAccountKey, setProvideAccountKey] = useState(false);
  const [offerReward, setOfferReward] = useState(false);

  // ✅ paid-check UI state
  const [paidCheckLoading, setPaidCheckLoading] = useState(false);
  const [isPaidGame, setIsPaidGame] = useState(false);
  const [paidAcknowledged, setPaidAcknowledged] = useState(false);

  useEffect(() => {
    fetchRequests();
    const tokenCheckInterval = setInterval(() => {
      const updatedToken = localStorage.getItem("token");
      setToken(updatedToken);
    }, 60000);
    return () => clearInterval(tokenCheckInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    filterRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, activeTab, requests]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, activeTab]);

  const filterRequests = () => {
    const filtered = requests.filter(
      (request) =>
        request.steamGameDetails?.name &&
        request.steamGameDetails.name
          .toLowerCase()
          .includes(searchQuery.toLowerCase())
    );
    setFilteredRequests(filterRequestsByStatus(filtered));
  };

  const paginatedRequests = filteredRequests.slice(
    (currentPage - 1) * requestsPerPage,
    currentPage * requestsPerPage
  );

  const totalPages = Math.ceil(filteredRequests.length / requestsPerPage);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const location = useLocation();

  useEffect(() => {
    try {
      const ins = document.querySelector(".adsbygoogle");
      if (ins && ins.innerHTML) ins.innerHTML = "";
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch (e) {
      console.error("AdSense error:", e);
    }
  }, [location.pathname]);

  const isValidSteamLink = (link) =>
    /^https:\/\/store\.steampowered\.com\/app\/\d+/.test(link);

  // ✅ check if the game is paid via your backend
  const checkIfPaid = async (link) => {
    const match = link.match(/\/app\/(\d+)/);
    if (!match) return;

    const appId = match[1];

    try {
      setPaidCheckLoading(true);
      setIsPaidGame(false);
      setPaidAcknowledged(false);

      const res = await axios.get(`/api/steam/appdetails/${appId}`);
      const data = res.data;

      // Steam: is_free === true means NOT paid
      if (data?.type === "game") {
        setIsPaidGame(data.is_free === false);
      }
    } catch (e) {
      console.error("Paid check failed:", e);
    } finally {
      setPaidCheckLoading(false);
    }
  };

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/api/requests");
      setRequests(res.data);
      setFilteredRequests(res.data);
    } catch (error) {
      console.error("Error fetching cheat requests:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    navigate("/login");
  };

  const refreshAccessToken = async () => {
    try {
      const refreshToken = localStorage.getItem("refreshToken");
      const response = await axios.post("/api/auth/refresh-token", {
        refreshToken,
      });
      const { accessToken } = response.data;

      localStorage.setItem("token", accessToken);
      return accessToken;
    } catch (err) {
      console.error("Failed to refresh access token. Logging out...");
      handleLogout();
      return null;
    }
  };

  const isTokenExpired = (tokenStr) => {
    try {
      const decodedToken = JSON.parse(atob(tokenStr.split(".")[1]));
      const currentTime = Date.now() / 1000;
      return decodedToken.exp < currentTime;
    } catch (error) {
      return true;
    }
  };

  const submitRequest = async () => {
    setErrorMessage("");

    // ✅ hard-block paid games unless acknowledged (prevents devtools bypass)
    if (isPaidGame && !paidAcknowledged) {
      setErrorMessage(
        "Please check the acknowledgement box for paid games before submitting."
      );
      return;
    }

    if (!token || isTokenExpired(token)) {
      const refreshedToken = await refreshAccessToken();
      if (!refreshedToken) return;
    }

    if (!steamLink || !isValidSteamLink(steamLink)) {
      setErrorMessage("Please enter a valid Steam store link.");
      return;
    }

    try {
      setLoading(true);

      const appIdMatch = steamLink.match(/\/app\/(\d+)/);
      if (!appIdMatch) {
        setErrorMessage("Unable to extract the app ID from the provided link.");
        return;
      }

      const appId = appIdMatch[1];

      const requestData = {
        steamLink: `https://store.steampowered.com/app/${appId}/`,
        userId: JSON.parse(atob(token.split(".")[1])).userId,
      };
      if (cheatDetails.trim()) requestData.cheatDetails = cheatDetails;

      await axios.post("/api/requests", requestData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // ✅ reset form state
      setSteamLink("");
      setCheatDetails("");
      setIsPaidGame(false);
      setPaidCheckLoading(false);
      setPaidAcknowledged(false);

      fetchRequests();
    } catch (error) {
      setErrorMessage(
        error.response?.data?.message ||
          "There was an error submitting the request. Please try again later."
      );
      console.error("Error submitting cheat request:", error);
    } finally {
      setLoading(false);
    }
  };

  const voteOnRequest = async (requestId) => {
    setErrorMessage("");

    if (!token) {
      const refreshedToken = await refreshAccessToken();
      if (!refreshedToken) return;
    }

    try {
      setVoteLoading((prev) => ({ ...prev, [requestId]: true }));
      await axios.post(
        `/api/requests/vote/${requestId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      fetchRequests();
    } catch (error) {
      setErrorMessage(
        error.response?.data?.message ||
          "There was an error voting. Please try again later."
      );
      console.error("Error voting on cheat request:", error);
    } finally {
      setVoteLoading((prev) => ({ ...prev, [requestId]: false }));
    }
  };

  const toggleForm = () => {
    setFormVisible(!formVisible);

    // optional: reset paid states when opening/closing form
    // (prevents weird carry-over if user closes form mid-entry)
    setPaidCheckLoading(false);
    setIsPaidGame(false);
    setPaidAcknowledged(false);
  };

  const filterRequestsByStatus = (requestsList = requests) => {
    if (activeTab === "approved") {
      return requestsList.filter(
        (request) =>
          request.status === "approved" || request.status === "completed"
      );
    } else if (activeTab === "rejected") {
      return requestsList.filter((request) => request.status === "rejected");
    } else if (activeTab === "pending") {
      return requestsList.filter((request) => request.status === "pending");
    }
    return requestsList.sort((a, b) => {
      const statusOrder = {
        pending: 1,
        approved: 2,
        completed: 3,
        rejected: 4,
      };
      return statusOrder[a.status] - statusOrder[b.status];
    });
  };

  const submitDisabled =
    loading || paidCheckLoading || (isPaidGame && !paidAcknowledged);

  return (
    <div className="cheatrequests-container">
      <Helmet key="cheat-requests-helmet">
        <title>Cheat Requests - Sintrix.net</title>
        <meta
          name="description"
          content="Submit and vote on cheat requests for games."
        />
        <meta property="og:title" content="Cheat Requests - Sintrix.net" />
        <meta
          property="og:description"
          content="Submit and vote on cheat requests for games."
        />
        <meta property="og:image" content="https://sintrix.net/sintrix2.png" />
        <meta property="og:url" content="https://sintrix.net/requests" />
      </Helmet>

      <h2 className="cheatrequests-title">Cheat Requests</h2>

      <div className="ad-container">
        <ins
          className="adsbygoogle"
          style={{ display: "block" }}
          data-ad-client="ca-pub-3980892685014254"
          data-ad-slot="4227089420"
          data-ad-format="auto"
          data-full-width-responsive="true"
        />
      </div>

      <input
        type="text"
        placeholder="Search requests..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        className="search-input"
      />

      <div className="tab-buttons">
        <button
          className={`tab-button ${activeTab === "all" ? "active" : ""}`}
          onClick={() => setActiveTab("all")}
        >
          All Requests
        </button>
        <button
          className={`tab-button ${activeTab === "approved" ? "active" : ""}`}
          onClick={() => setActiveTab("approved")}
        >
          Approved
        </button>
        <button
          className={`tab-button ${activeTab === "rejected" ? "active" : ""}`}
          onClick={() => setActiveTab("rejected")}
        >
          Rejected
        </button>
        <button
          className={`tab-button ${activeTab === "pending" ? "active" : ""}`}
          onClick={() => setActiveTab("pending")}
        >
          Pending
        </button>
        <button onClick={toggleForm} className="cheatrequests-toggle-button">
          <FontAwesomeIcon icon={faSquarePlus} size="1x" />
        </button>
      </div>

      {errorMessage && <p className="global-error">{errorMessage}</p>}

      {formVisible && (
        <div className="cheatrequests-input-container">
          <input
            type="text"
            placeholder="Steam Store Link"
            value={steamLink}
            onChange={(e) => {
              const val = e.target.value;
              setSteamLink(val);

              // always reset acknowledgement when the link changes
              setPaidAcknowledged(false);

              if (isValidSteamLink(val)) {
                checkIfPaid(val);
              } else {
                setIsPaidGame(false);
                setPaidCheckLoading(false);
                setPaidAcknowledged(false);
              }
            }}
            className="cheatrequests-input"
          />

          {paidCheckLoading && (
            <p style={{ marginTop: "6px", fontSize: "13px", opacity: 0.85 }}>
              Checking if the game is paid...
            </p>
          )}

          {!paidCheckLoading && isPaidGame && (
            <div
              style={{
                marginBottom: "8px",
                padding: "10px 12px",
                borderRadius: "10px",
                background: "rgba(255, 0, 0, 0.12)",
                border: "1px solid rgba(255, 51, 0, 0.25)",
                fontSize: "13px",
                lineHeight: "1.35",
              }}
            >
              <div style={{ marginBottom: "8px" }}>
                This game is paid, contact us through{" "}
                <a
                  href="https://discord.gg/zjvdGQBrBc"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#7289da", fontWeight: "600" }}
                >
                  Discord
                </a>{" "}
                to either family share the game or gift it as we may not own it.
              </div>

                <label className="paid-ack-row">
                <input
                    type="checkbox"
                    checked={paidAcknowledged}
                    onChange={(e) => setPaidAcknowledged(e.target.checked)}
                />
                <span className="paid-ack-box">
                    <span className="paid-ack-check" />
                </span>
                <span>I understand and will contact the Sintrix team on Discord.</span>
                </label>
            </div>
          )}

          <input
            type="text"
            placeholder="Extra Details (optional)"
            value={cheatDetails}
            onChange={(e) => setCheatDetails(e.target.value)}
            className="cheatrequests-input"
          />

          <button
            onClick={submitRequest}
            disabled={submitDisabled}
            className="cheatrequests-button"
            style={{
              opacity: submitDisabled ? 0.5 : 1,
              cursor: submitDisabled ? "not-allowed" : "pointer",
            }}
          >
            {loading ? "Submitting..." : "Submit Request"}
          </button>
        </div>
      )}

      {loading && <p>Loading requests...</p>}

      {paginatedRequests.length === 0 && !loading ? (
        <p>No requests match your search.</p>
      ) : (
        paginatedRequests.map((request) => (
          <div key={request._id} className={`cheatrequests-card ${request.status}`}>
            <div className="game-info">
              <p>
                <strong>Game:</strong>{" "}
                {request.steamGameDetails?.name ? (
                  <a
                    href={`https://store.steampowered.com/app/${request.gameId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="steam-link"
                  >
                    {request.steamGameDetails.name}
                  </a>
                ) : (
                  "Unknown Game"
                )}
              </p>

              <p>
                <strong>SteamDB Link:</strong>{" "}
                <a
                  className="steam-link"
                  href={request.steamDBLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {request.steamDBLink}
                </a>
              </p>

              <p className="request-by">
                <strong>Request by:</strong>{" "}
                {request.username === "Anonymous" ? (
                  <span className="anonymous-request">Anonymous</span>
                ) : (
                  <Link to={`/profile/${request.username}`} className="profile-link-request">
                    {request.username}
                  </Link>
                )}
              </p>

              {request.status === "rejected" && request.rejectionReason && (
                <p>
                  <strong>Rejection Reason:</strong> {request.rejectionReason}
                </p>
              )}

              {request.status === "completed" && (
                <p>
                  This request is complete! You can find the table in{" "}
                  <a className="steam-link" href="/games">
                    Tables
                  </a>
                  .
                </p>
              )}
            </div>

            <div className="vote-section">
              <p>
                <strong>Votes:</strong> {request.votes}
              </p>
              {request.status === "pending" && (
                <button
                  onClick={() => voteOnRequest(request._id)}
                  disabled={voteLoading[request._id]}
                  className="cheatrequests-vote-button"
                >
                  {voteLoading[request._id] ? "Voting..." : "Vote"}
                </button>
              )}
            </div>
          </div>
        ))
      )}

      <div className="pagination-controls">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="pagination-button"
        >
          Previous
        </button>
        <span className="pagination-text">
          Page {currentPage} of {totalPages}
        </span>
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="pagination-button"
        >
          Next
        </button>
      </div>
    </div>
  );
}

export default CheatRequests;
